// Booking detail. Markup mirrors 11-booking-detail.html: .detail-wrap with
// .crumb-line, .detail-head (.detail-pnr + status-pill), .detail-sub,
// .detail-grid (left .itin-card stack + right summary cards).

import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ApiError, api } from "../lib/api";
import { lookupAirport } from "../lib/airports";
import { fmtCabin, fmtPaxType } from "../lib/labels";
import { useBodyClass } from "../lib/useBodyClass";
import { AirlineLogo } from "../components/AirlineLogo";
import type { BookingListItem, Itinerary } from "../lib/types";

export function BookingDetail() {
  useBodyClass("has-bg2");
  const { bookingId } = useParams();

  const meta = useQuery({
    queryKey: ["bookingMeta", bookingId],
    queryFn: () => api.get<BookingListItem>(`/v1/bookings/${bookingId}/`),
    enabled: !!bookingId,
  });
  const itin = useQuery({
    queryKey: ["booking", bookingId],
    queryFn: () => api.get<Itinerary>(`/v1/flights/booking/${bookingId}/`),
    enabled: !!bookingId,
    retry: false,
  });

  if (meta.isLoading) return <div className="app-loading"><span className="spinner" />Loading booking…</div>;
  if (meta.error || !meta.data) return <div className="app-error">Couldn't load booking.</div>;

  const b = meta.data;
  const passengers = b.passengers || [];

  return (
    <div className="container">
      <div className="detail-wrap">
        <div className="crumb-line">
          <Link to="/bookings">My bookings</Link>
          <span className="sep">›</span>
          <b>{b.pnr || b.id.slice(0, 8)}</b>
        </div>

        <div className="detail-head">
          <div className="detail-pnr">{b.pnr || "—"}</div>
          <span className={`status-pill ${statusClass(b.status)}`}>
            <span className="dot" />{statusLabel(b.status)}
          </span>
        </div>
        <p className="detail-sub">
          Booking ID <b>{b.id.slice(0, 8)}</b>
          {" "}&nbsp;·&nbsp; Issued <b>{fmtIssued(b.modified_at)}</b>
        </p>

        <div className="detail-grid">
          {/* LEFT — itinerary, passengers, manage */}
          <div>
            <div className="itin-card">
              <h3>Your <span className="serif">itinerary</span></h3>
              <div className="itin-card-sub">
                {summaryRoute(itin.data, b)} · {paxLabel(b)}
                {" · "}{itin.data?.itinerary_items[0]?.is_refundable ? "refundable" : "non-refundable"}{" "}
                {fmtCabin(itin.data?.itinerary_items[0]?.legs[0]?.segments[0]?.cabin_class)}
              </div>

              {itin.isLoading && <div style={{ color: "var(--ink-3)", fontSize: 13 }}><span className="spinner" />Loading live itinerary…</div>}
              {itin.error instanceof ApiError && (
                <div style={{ color: "var(--ink-3)", fontSize: 13 }}>Couldn't refresh from upstream — showing what we have stored.</div>
              )}

              {itin.data?.itinerary_items[0]?.legs.map((leg, li) => (
                <div className="segment" key={li}>
                  <div className="seg-dir">{capitalize(leg.direction)} · {fmtDateLong(leg.segments[0]?.origin.departure_time)}</div>
                  {leg.segments.map((s, si) => (
                    <div key={si}>
                      <div className="segment-row">
                        <div className="when"><b>{fmtTime(s.origin.departure_time)}</b><span>{fmtShort(s.origin.departure_time)}</span></div>
                        <div className="at"><b>{lookupAirport(s.origin.airport_code).city} · {lookupAirport(s.origin.airport_code).name}</b><span>{s.origin.terminal ? `Terminal ${s.origin.terminal}` : ""}</span></div>
                      </div>
                      <div className="segment-flight">
                        <AirlineLogo code={s.airline.code} name={s.airline.name} />
                        <span><b>{s.airline.code} {s.airline.flight_number} · {s.airline.name}</b> · {fmtCabin(s.cabin_class)} · {fmtDuration(s.duration_minutes)} · {leg.stop_count === 0 ? "direct" : `${leg.stop_count} stop`}</span>
                      </div>
                      <div className="segment-row">
                        <div className="when"><b>{fmtTime(s.destination.arrival_time)}</b><span>{fmtShort(s.destination.arrival_time)}</span></div>
                        <div className="at"><b>{lookupAirport(s.destination.airport_code).city} · {lookupAirport(s.destination.airport_code).name}</b><span>{s.destination.terminal ? `Terminal ${s.destination.terminal}` : ""}</span></div>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>

            <div className="itin-card">
              <h3>Passengers <span className="serif">on the ticket</span></h3>
              <div className="itin-card-sub">Names cannot be edited after ticketing — contact us if anything is wrong.</div>
              {passengers.length === 0 ? (
                <div style={{ color: "var(--ink-3)", fontSize: 13 }}>No passengers recorded against this booking.</div>
              ) : passengers.map((p, i) => {
                const name = [p.title, p.first_name, p.last_name].filter(Boolean).map(String).join(" ");
                const meta = [
                  fmtPaxType(String(p.pax_type ?? "adult")),
                  p.nationality ? String(p.nationality) : null,
                  p.passport_number ? `Passport ${String(p.passport_number)}` : null,
                ].filter(Boolean).join(" · ");
                return (
                  <div className="passenger-line" key={i}>
                    <div>
                      <b>{name}</b>
                      <span>{meta}</span>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="itin-card">
              <h3>Manage <span className="serif">this booking</span></h3>
              <div className="itin-card-sub">Cancellation / change endpoints aren't wired in this build.</div>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <button type="button" className="btn btn-ghost btn-pill">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" /></svg>
                  Download e-ticket
                </button>
                <button type="button" className="btn btn-ghost btn-pill">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2zM22 6l-10 7L2 6" /></svg>
                  Email again
                </button>
              </div>
            </div>
          </div>

          {/* RIGHT — payment + references */}
          <aside>
            <div className="summary-card">
              <h3>Payment</h3>
              <div className="sub">Captured from wallet · {fmtIssued(b.modified_at)}</div>
              <div className="fare-line"><span className="lbl">Method</span><span>Partner wallet</span></div>
              <div className="fare-line"><span className="lbl">Status</span>
                <span className={`status-pill ${statusClass(b.status)}`}><span className="dot" />{statusLabel(b.status)}</span>
              </div>
              <div style={{ height: 1, background: "var(--line)", margin: "14px 0" }} />
              <div className="fare-line"><span className="lbl">Charged</span><span>{b.amount_charged ? fmtMoney(parseFloat(b.amount_charged), b.currency) : "—"}</span></div>
              {b.wallet_debited_inr && (
                <div className="fare-line"><span className="lbl">Wallet debit (INR)</span><span>{fmtMoney(parseFloat(b.wallet_debited_inr), "INR")}</span></div>
              )}
            </div>

            <div className="summary-card" style={{ marginTop: 14 }}>
              <h3>References</h3>
              <div className="fare-line"><span className="lbl">PNR</span><span style={{ fontFamily: "ui-monospace, SFMono-Regular, monospace" }}>{b.pnr || "—"}</span></div>
              <div className="fare-line"><span className="lbl">Itinerary code</span><span style={{ fontFamily: "ui-monospace, SFMono-Regular, monospace", fontSize: 12 }}>{b.itinerary_code || "—"}</span></div>
              <div className="fare-line"><span className="lbl">Trace ID</span><span style={{ fontFamily: "ui-monospace, SFMono-Regular, monospace", fontSize: 12, wordBreak: "break-all" }}>{b.trace_id || "—"}</span></div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}

// ─── helpers ────────────────────────────────────────────────────────────────

function statusClass(s: string): string {
  switch (s) {
    case "confirmed": return "confirmed";
    case "draft": return "draft";
    case "passenger_saved":
    case "priced": return "pending";
    case "cancelled": return "refunded";
    case "failed": return "failed";
    default: return "draft";
  }
}
function statusLabel(s: string): string {
  switch (s) {
    case "confirmed": return "Confirmed · ticketed";
    case "draft": return "Draft";
    case "passenger_saved": return "Pax saved · pending pay";
    case "priced": return "Repriced · pending pay";
    case "cancelled": return "Cancelled";
    case "failed": return "Failed · refunded";
    default: return s;
  }
}
function summaryRoute(itin: Itinerary | undefined, b: BookingListItem): string {
  if (itin?.itinerary_items[0]) {
    const item = itin.itinerary_items[0];
    const o = item.legs[0].segments[0].origin.airport_code;
    const last = item.legs[item.legs.length - 1];
    const z = last.segments[last.segments.length - 1].destination.airport_code;
    if (z === o) {
      const mid = item.legs[0].segments[item.legs[0].segments.length - 1].destination.airport_code;
      return `${lookupAirport(o).city} ⇄ ${lookupAirport(mid).city}`;
    }
    return `${lookupAirport(o).city} → ${lookupAirport(z).city}`;
  }
  // Fall back to stored segments
  const segs = b.flight_segments || [];
  if (segs.length === 0) return "—";
  const o = (segs[0].origin as Record<string, unknown>)?.airport_code as string;
  const z = (segs[segs.length - 1].destination as Record<string, unknown>)?.airport_code as string;
  return `${lookupAirport(o || "").city} → ${lookupAirport(z || "").city}`;
}
function paxLabel(b: BookingListItem): string {
  const n = (b.passengers || []).length;
  if (n === 0) return "1 passenger";
  return `${n} ${n === 1 ? "passenger" : "passengers"}`;
}
function fmtIssued(iso: string): string {
  const d = new Date(iso); if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short" }) + " · " + d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: false });
}
function fmtTime(iso?: string): string {
  if (!iso) return "—";
  const d = new Date(iso); return Number.isNaN(d.getTime()) ? "—" : d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: false });
}
function fmtDateLong(iso?: string): string {
  if (!iso) return "";
  const d = new Date(iso); return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" });
}
function fmtShort(iso?: string): string {
  if (!iso) return "";
  const d = new Date(iso); return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
}
function fmtDuration(mins: number): string {
  if (!Number.isFinite(mins)) return "—";
  const h = Math.floor(mins / 60); const m = mins % 60;
  return h === 0 ? `${m}m` : m === 0 ? `${h}h` : `${h}h ${String(m).padStart(2, "0")}m`;
}
function fmtMoney(n: number, currency: string): string {
  try { return new Intl.NumberFormat("en-IN", { style: "currency", currency, maximumFractionDigits: 0 }).format(n); }
  catch { return `${currency} ${Math.round(n).toLocaleString("en-IN")}`; }
}
function capitalize(s: string): string { return s ? s[0].toUpperCase() + s.slice(1) : s; }
